"use client";

import React, { createContext, useMemo, useRef, useState } from "react";
import { useEvents } from "./pusher";
// import { applyWorkflowRunRealtimePatch } from "@store/features/settings/workflowSlice";
import {
  GetWorkflowRunSchema,
  type GetWorkflowRunType,
} from "skedyul-core/features/workflows/features/workflowRuns/resources/types";
import type { BuildLogEntry } from "skedyul-core/features/workflows/resources/types";
// Local lightweight summary type to avoid unsafe-any from external type resolution
type WorkflowRunSummaryLocal = {
  runId: string;
  workflowId: string;
  workflowName: string;
  status: "queued" | "running" | "completed" | "failed" | "canceled";
  updatedAt: string;
};

function isWorkflowRunSummaryLike(
  val: unknown,
): val is WorkflowRunSummaryLocal {
  if (!val || typeof val !== "object") return false;
  const obj = val as Record<string, unknown>;
  return (
    typeof obj["runId"] === "string" &&
    typeof obj["workflowId"] === "string" &&
    typeof obj["workflowName"] === "string" &&
    typeof obj["status"] === "string" &&
    typeof obj["updatedAt"] === "string"
  );
}
// import { getWorkflowRun } from "@store/features/settings/workflowSlice";
import { trpc } from "@utils/api";
import { store } from "@store/index";
import { updateParams as updateParamsAction } from "@store/features/layout/sideNavigationSlice";

type ActiveWorkflowsContextValue = {
  activeWorkflows: ReadonlyArray<GetWorkflowRunType>;
};

const ActiveWorkflowsContext = createContext<ActiveWorkflowsContextValue>({
  activeWorkflows: [],
});

// Parse incoming events with full GetWorkflowRunSchema

export function WorkflowsProvider({ children }: { children: React.ReactNode }) {
  // Internal state keyed by workflowRunId
  const [byRunId, setByRunId] = useState<
    Map<string, GetWorkflowRunType & { __order: number }>
  >(() => new Map());
  const lastOrderRef = useRef<number>(0);

  const handleWorkflowUpdated = React.useCallback((data: unknown) => {
    const env =
      typeof data === "object" && data !== null
        ? (data as Record<string, unknown>)
        : undefined;
    const payload = env && "payload" in env ? env["payload"] : undefined;

    if (!payload || typeof payload !== "object") return;

    const removed = (payload as { removed?: unknown }).removed === true;
    const parsed = GetWorkflowRunSchema.safeParse(
      (payload as { workflowRun?: unknown }).workflowRun,
    );
    if (!parsed.success) {
      const candidate = (payload as { workflowRun?: unknown }).workflowRun;
      if (isWorkflowRunSummaryLike(candidate)) {
        // Fallback: upsert summary-shaped payload
        const summary = candidate;
        const runId = summary.runId;
        if (!runId) return;
        const nowIso = new Date().toISOString();
        const completedAt =
          summary.status === "completed" ? summary.updatedAt : null;
        const cancelledAt =
          summary.status === "canceled" ? summary.updatedAt : null;
        const failedAt = summary.status === "failed" ? summary.updatedAt : null;
        const startedAt =
          summary.status === "running" ? summary.updatedAt : null;
        const runLike = {
          id: summary.runId,
          workflowId: summary.workflowId,
          workflowVersionId: "",
          createdAt: summary.updatedAt || nowIso,
          updatedAt: summary.updatedAt || nowIso,
          startedAt,
          failedAt,
          completedAt,
          cancelledAt,
          workflow: {
            id: summary.workflowId,
            name: summary.workflowName,
          },
          workflowVersion: { id: "", version: "0", versionLabel: "v0" },
          steps: [],
          inputs: {},
          pausedAt: null,
        } as unknown as GetWorkflowRunType;
        setByRunId((prev) => {
          const next = new Map(prev);
          next.set(runLike.id, {
            ...runLike,
            __order: (lastOrderRef.current += 1),
          } as GetWorkflowRunType & { __order: number });
          return next;
        });
      }
      return;
    }
    const workflowRun: GetWorkflowRunType = parsed.data;

    const runId = workflowRun.id;
    if (!runId) return;

    setByRunId((prev) => {
      const next = new Map(prev);
      if (removed) {
        next.delete(runId);
        return next;
      }

      next.set(runId, {
        ...workflowRun,
        __order: (lastOrderRef.current += 1),
      } as GetWorkflowRunType & { __order: number });
      return next;
    });
  }, []);

  // Summary is handled as a fallback inside handleWorkflowUpdated

  // Listen for centralized workplace-scope workflow updates dispatched by UserProvider
  React.useEffect(() => {
    const onWorkflowUpdated = (ev: Event) => {
      try {
        const detail = (ev as CustomEvent).detail as unknown;
        (handleWorkflowUpdated as (d: unknown) => void)(detail);
      } catch {
        // ignore event errors
      }
    };
    if (typeof window === "undefined") return;
    window.addEventListener(
      "workflow.updated",
      onWorkflowUpdated as EventListener,
    );
    return () => {
      window.removeEventListener(
        "workflow.updated",
        onWorkflowUpdated as EventListener,
      );
    };
  }, [handleWorkflowUpdated]);

  const value = useMemo<ActiveWorkflowsContextValue>(() => {
    const items = Array.from(byRunId.values()).map(
      ({ __order, ...rest }) => rest as GetWorkflowRunType,
    );
    items.sort((a, b) => {
      // Running first
      const aRunning = !(a.completedAt || a.cancelledAt);
      const bRunning = !(b.completedAt || b.cancelledAt);
      if (aRunning !== bRunning) return aRunning ? -1 : 1;
      // Then by recency using latest timestamp we have
      const aLast = a.steps?.[a.steps.length - 1];
      const bLast = b.steps?.[b.steps.length - 1];
      const at =
        (aLast?.startedAt ||
          aLast?.scheduledAt ||
          aLast?.completedAt ||
          a.completedAt ||
          a.cancelledAt) ??
        "";
      const bt =
        (bLast?.startedAt ||
          bLast?.scheduledAt ||
          bLast?.completedAt ||
          b.completedAt ||
          b.cancelledAt) ??
        "";
      return String(at).localeCompare(String(bt));
    });
    return { activeWorkflows: items };
  }, [byRunId]);

  return (
    <ActiveWorkflowsContext.Provider value={value}>
      {children}
    </ActiveWorkflowsContext.Provider>
  );
}

export function useActiveWorkflows(args: {
  modelHandle: string;
  instanceId?: string;
}): ReadonlyArray<{
  runId: string;
  workflowId: string;
  workflowName: string;
  status: "queued" | "running" | "completed" | "failed" | "canceled";
  updatedAt: string;
}> {
  const { modelHandle, instanceId } = args;

  const [byRunId, setByRunId] = useState<
    Map<string, WorkflowRunSummaryLocal & { __order: number }>
  >(() => new Map());
  const lastOrderRef = useRef<number>(0);

  // Redux dispatching for run-specific stream happens in useStreamWorkflowRun

  const handleWorkflowUpdated = React.useCallback(
    (data: unknown) => {
      const env =
        typeof data === "object" && data !== null
          ? (data as Record<string, unknown>)
          : undefined;
      const hint =
        env && typeof env["hint"] === "object"
          ? (env["hint"] as {
              workflowRunId?: unknown;
              instanceIds?: unknown[];
            })
          : undefined;
      const payload = env && "payload" in env ? env["payload"] : undefined;

      // If filtering by instanceId, ensure the event relates to it using hint.instanceIds
      if (typeof instanceId === "string" && instanceId && hint) {
        const hintIds: unknown[] = Array.isArray(hint.instanceIds)
          ? hint.instanceIds
          : [];
        const inHint = hintIds.some((v) => v === instanceId);
        if (!inHint) return;
      }

      // Ignore hydrate fan-out; list contexts should not fetch details
      if (!payload || typeof payload !== "object") return;

      const removed = (payload as { removed?: unknown }).removed === true;
      const parsed = GetWorkflowRunSchema.safeParse(
        (payload as { workflowRun?: unknown }).workflowRun,
      );
      if (!parsed.success) {
        // Fallback to summary-shaped workflowRun
        const candidate = (payload as { workflowRun?: unknown }).workflowRun;
        if (isWorkflowRunSummaryLike(candidate)) {
          const summary = candidate;
          const runId = summary.runId;
          if (!runId) return;
          const runLike: WorkflowRunSummaryLocal = {
            runId: summary.runId,
            workflowId: summary.workflowId,
            workflowName: summary.workflowName,
            status: summary.status,
            updatedAt: summary.updatedAt,
          };
          setByRunId((prev) => {
            const next = new Map(prev);
            next.set(runId, {
              ...runLike,
              __order: (lastOrderRef.current += 1),
            } as WorkflowRunSummaryLocal & { __order: number });
            return next;
          });
        }
        return;
      }
      const workflowRun: GetWorkflowRunType = parsed.data;
      // Convert full run to summary
      const runSummary: WorkflowRunSummaryLocal = {
        runId: workflowRun.id,
        workflowId: workflowRun.workflow.id,
        workflowName: workflowRun.workflow.name,
        status:
          workflowRun.completedAt != null
            ? "completed"
            : (workflowRun as { failedAt?: string | null })?.failedAt != null
              ? "failed"
              : workflowRun.cancelledAt != null
                ? "canceled"
                : workflowRun.startedAt != null
                  ? "running"
                  : "queued",
        updatedAt: String(workflowRun.updatedAt),
      };

      const runId = workflowRun.id;
      if (!runId) return;

      setByRunId((prev) => {
        const next = new Map(prev);
        if (removed) {
          next.delete(runId);
          return next;
        }

        next.set(runId, {
          ...runSummary,
          __order: (lastOrderRef.current += 1),
        } as WorkflowRunSummaryLocal & { __order: number });
        return next;
      });
    },
    [instanceId],
  );

  // Removed separate summary handler; summary fallback handled in handleWorkflowUpdated

  // Subscribe to relevant workflow events for model + workplace scope
  useEvents(
    useMemo(
      () => [
        // Subscribe to workflow-run specific channel when applicable
        // Note: runId is not in scope here; this subscription is intended for contexts
        // where a specific run id is known. It is disabled by default.
        // Consumers should instead subscribe at the page/layout level with the concrete run id.
        {
          channel: ({ subdomain }) =>
            subdomain && !instanceId
              ? `private-${subdomain}-model-${modelHandle}`
              : "",
          events: {
            "workflow.updated": handleWorkflowUpdated as unknown as (
              d: unknown,
            ) => void,
          },
        },
        {
          channel: ({ subdomain }) =>
            subdomain && instanceId
              ? `private-${subdomain}-instance-${instanceId}`
              : "",
          events: {
            "workflow.updated": handleWorkflowUpdated as unknown as (
              d: unknown,
            ) => void,
          },
        },
      ],
      [modelHandle, instanceId, handleWorkflowUpdated],
    ),
  );

  const items = useMemo(() => {
    const arr = Array.from(byRunId.values()).map(
      ({ __order, ...rest }) => rest as WorkflowRunSummaryLocal,
    );
    // Running first, then by updatedAt desc
    arr.sort((a, b) => {
      const aRunning = a.status === "running" || a.status === "queued";
      const bRunning = b.status === "running" || b.status === "queued";
      if (aRunning !== bRunning) return aRunning ? -1 : 1;
      return String(b.updatedAt).localeCompare(String(a.updatedAt));
    });
    return arr;
  }, [byRunId]);

  return items;
}

// Subscribe to a specific workflow run channel and invoke an optional callback on updates
export function useStreamWorkflowRun(
  runId?: string | null,
  onUpdated?: (data: unknown) => void,
): void {
  const handleWorkflowUpdatedViaRedux = React.useCallback(
    (data: unknown) => {
      try {
        const env =
          typeof data === "object" && data !== null
            ? (data as Record<string, unknown>)
            : undefined;
        const hydrate = Boolean(
          env && typeof env.hydrate === "boolean" && env.hydrate === true,
        );
        const hint =
          env && typeof env["hint"] === "object" && env["hint"] !== null
            ? (env["hint"] as { workflowRunId?: unknown })
            : undefined;
        const payload = env && "payload" in env ? env["payload"] : undefined;

        if (hydrate) {
          const hintedRunId =
            typeof hint?.workflowRunId === "string"
              ? hint.workflowRunId
              : undefined;
          if (hintedRunId) {
            if (typeof onUpdated === "function") onUpdated(data);
            return;
          }
        }

        if (payload && typeof payload === "object") {
          // Accept either direct payload as GetWorkflowRun or nested payload.workflowRun
          const direct = GetWorkflowRunSchema.safeParse(payload);
          const nested = GetWorkflowRunSchema.safeParse(
            (payload as { workflowRun?: unknown }).workflowRun,
          );
          if (direct.success || nested.success) {
            // validation succeeded; no-op here since consumer callback handles it
          }
        }

        if (typeof onUpdated === "function") onUpdated(data);
      } catch {
        // ignore consumer errors
      }
    },
    [onUpdated],
  );

  useEvents(
    useMemo(
      () =>
        runId
          ? [
              {
                channel: ({ subdomain }) =>
                  subdomain && runId
                    ? `private-${subdomain}-workflow-${runId}`
                    : "",
                events: {
                  "workflow.updated":
                    handleWorkflowUpdatedViaRedux as unknown as (
                      d: unknown,
                    ) => void,
                },
              },
            ]
          : [],
      [runId, handleWorkflowUpdatedViaRedux],
    ),
  );
}

// NOTE: useStreamBuildLogs and useCheckBuildCompletion have been removed.
// Build logs are now handled via the deployments slice and DEPLOYMENT_LOGS sidebar.
// See: useStreamDeploymentLogs in @providers/deployments
